import { useCallback, useRef } from 'react';
import { waitForRegisteredNavigator } from '@/core/widgets/model/widget-host';

export interface WidgetNavigatorRegistry {
  registerWidgetNavigator(id: string, navigate: (path: string) => void): () => void;
  // Navigates immediately if a navigator is already registered for `id`;
  // otherwise waits for one to register (see waitForRegisteredNavigator).
  // Returns false (and warns) if none ever registers.
  navigateOrWait(id: string, path: string): Promise<boolean>;
}

export default function useWidgetNavigatorRegistry(): WidgetNavigatorRegistry {
  const navigatorsRef = useRef(new Map<string, (path: string) => void>());

  const registerWidgetNavigator = useCallback((id: string, navigate: (path: string) => void) => {
    navigatorsRef.current.set(id, navigate);
    return () => {
      if (navigatorsRef.current.get(id) === navigate) navigatorsRef.current.delete(id);
    };
  }, []);

  const navigateOrWait = useCallback(async (id: string, path: string) => {
    const existing = navigatorsRef.current.get(id);
    if (existing) {
      existing(path);
      return true;
    }

    const navigate = await waitForRegisteredNavigator(navigatorsRef.current, id);
    if (navigate) {
      navigate(path);
      return true;
    }

    console.warn('Widget navigator never registered for', id);
    return false;
  }, []);

  return { registerWidgetNavigator, navigateOrWait };
}
